import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import CollectCta from "@/components/the190/CollectCta";
import { buildFaq, storeUrl } from "@/lib/the190";
import { track, trackOutbound } from "@/lib/analytics";
import { followArchive } from "@/lib/api";

/* --------------------------- COLLECTING WITH PURPOSE --------------------------- */

export const Purpose190 = () => (
  <section
    id="purpose"
    data-testid="the190-purpose"
    className="bone-section py-24 md:py-36"
    style={{ scrollMarginTop: 80 }}
  >
    <div className="max-w-[1100px] mx-auto px-6 md:px-12">
      <Reveal slow>
        <p className="hairline opacity-60 mb-6">A Note on Purpose</p>
        <h2 className="font-wordmark text-4xl md:text-6xl leading-[1.05] max-w-3xl">
          Collecting with purpose.
        </h2>
      </Reveal>
      <Reveal delay={100} className="mt-10 max-w-3xl">
        <p className="font-serif italic text-lg md:text-xl leading-[1.55] opacity-80">
          Acquiring a Collector Edition also contributes to the ongoing
          preservation, documentation and public presentation of the Nick Fraser
          Archive — supporting research, exhibitions, film and the continued work
          of bringing Nick&apos;s extraordinary body of work into public view.
        </p>
      </Reveal>
      <Reveal className="mt-12" slow>
        <a
          href="https://www.nickfraserartist.com/nick-fraser-artist"
          onClick={() => trackOutbound("purpose-archive")}
          className="hairline underline underline-offset-8 opacity-80 hover:opacity-100 transition-opacity duration-500 inline-block"
          data-testid="the190-purpose-cta"
        >
          Discover the Archive →
        </a>
      </Reveal>
    </div>
  </section>
);

/* --------------------------- FAQ --------------------------- */

export const Faq190 = ({ data }) => {
  const [open, setOpen] = useState(null);
  const faq = buildFaq(data);
  return (
    <section className="bone-section py-28 md:py-44" data-testid="the190-faq">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <Reveal slow>
          <p className="hairline opacity-60 mb-6">Questions</p>
          <h2 className="font-wordmark text-5xl md:text-7xl leading-[1.02]">
            Before you collect.
          </h2>
        </Reveal>
        <div className="mt-14 border-t border-[color:var(--rule)]">
          {faq.map((f, i) => (
            <div
              key={f.q}
              className="border-b border-[color:var(--rule)]"
              data-testid={`the190-faq-${i}`}
            >
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-6 py-6 text-left"
                data-testid={`the190-faq-toggle-${i}`}
                aria-expanded={open === i}
              >
                <span className="hairline !text-[0.75rem]">{f.q}</span>
                <span className="font-serif text-2xl leading-none opacity-50">
                  {open === i ? "−" : "+"}
                </span>
              </button>
              {open === i && (
                <div className="pb-7 -mt-1" data-testid={`the190-faq-answer-${i}`}>
                  <p className="font-serif italic text-lg md:text-xl leading-[1.5] opacity-80 max-w-2xl">
                    {f.a}
                  </p>
                  {f.link ? (
                    <a
                      href={storeUrl("faq")}
                      onClick={() => trackOutbound("faq")}
                      className="hairline underline underline-offset-8 mt-4 inline-block"
                      data-testid="the190-faq-store-link"
                    >
                      Visit the official store
                    </a>
                  ) : null}
                  {f.contact ? (
                    <Link
                      to="/contact?topic=acquisition"
                      className="hairline underline underline-offset-8 mt-4 inline-block"
                      data-testid="the190-faq-contact-link"
                    >
                      Make an enquiry
                    </Link>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------------------- FINAL ACQUISITION --------------------------- */

const AcquisitionCard = ({ title, spec, price, href, testid, prominent }) => (
  <div
    data-testid={testid}
    className={`border-t ${prominent ? "border-[color:var(--ochre)]/60 md:col-span-2" : "border-white/15"} pt-8 md:pt-10`}
  >
    <p className={`hairline ${prominent ? "text-[color:var(--ochre)]" : "opacity-60"} mb-4`}>
      {prominent ? "The Complete Chapter II Pairing" : "Chapter II Collector Edition"}
    </p>
    <h3
      className={`font-wordmark leading-[1.02] ${prominent ? "text-5xl md:text-7xl" : "text-3xl md:text-5xl"}`}
    >
      {title}
    </h3>
    <p className="hairline opacity-60 mt-4">{spec}</p>
    <p
      className={`font-serif italic mt-8 ${prominent ? "text-5xl md:text-6xl" : "text-4xl md:text-5xl"}`}
    >
      {price}
    </p>
    <div className="mt-8">
      <CollectCta
        label={prominent ? `Collect the Pair — ${price}` : "Collect →"}
        content={testid}
        href={href}
        dark
        testid={`${testid}-cta`}
      />
    </div>
  </div>
);

export const FinalAcquisition190 = ({ data }) => (
  <section
    id="collect"
    data-testid="the190-final-acquisition"
    className="text-[color:var(--bone)] py-28 md:py-44"
    style={{ backgroundColor: "#0e0e0e" }}
  >
    <div className="max-w-[1500px] mx-auto px-6 md:px-12">
      <Reveal slow>
        <p className="hairline opacity-55 mb-6">Now Available</p>
        <h2 className="font-wordmark text-5xl md:text-8xl leading-[1.02]">
          Collect an Edition.
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16 mt-20 md:mt-28">
        <AcquisitionCard
          title="Tribal Goddess"
          spec="Signed · Numbered · Edition of 100"
          price={data?.price_single || "£199"}
          href={data?.edition_one?.link}
          testid="the190-final-tg"
        />
        <AcquisitionCard
          title="Universal Soul of Stars"
          spec="Signed · Numbered · Edition of 100"
          price={data?.price_single || "£199"}
          href={data?.edition_two?.link}
          testid="the190-final-us"
        />
        <AcquisitionCard
          title="The Collector Pair"
          spec="The complete Chapter II pairing · Two Certificates of Authenticity"
          price={data?.price_pair || "£350"}
          href={data?.pair_link}
          testid="the190-final-pair"
          prominent
        />
      </div>

      <Reveal className="mt-16 md:mt-20" slow>
        <p className="hairline opacity-55 text-center md:text-left">
          Certificate of Authenticity included with every edition.
        </p>
      </Reveal>
    </div>
  </section>
);

/* --------------------------- NEWSLETTER --------------------------- */

const FollowArchive = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await followArchive(firstName.trim(), email.trim());
      if (res?.ok) {
        track("email_signup", { source: "collector-editions" });
        if (window.fbq) window.fbq("track", "Lead", { content_name: "collector-editions-newsletter" });
        toast(res.already ? "You are already following the archive." : "You are following the story.");
        setFirstName("");
        setEmail("");
      }
    } catch {
      toast("We couldn't add you. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-12" data-testid="the190-newsletter">
      <Reveal slow>
        <p className="hairline opacity-60 mb-5">Follow the Archive</p>
        <h3 className="font-serif italic text-3xl md:text-4xl leading-[1.15] max-w-xl">
          Receive new archive discoveries, films, exhibition announcements and future
          collector releases.
        </h3>
        <form
          onSubmit={onSubmit}
          className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end max-w-2xl"
        >
          <div className="md:col-span-4">
            <label className="hairline opacity-50 block mb-2" htmlFor="newsletter-first-name">
              First name
            </label>
            <input
              id="newsletter-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="invisible-input"
              data-testid="the190-newsletter-name"
              autoComplete="given-name"
            />
          </div>
          <div className="md:col-span-5">
            <label className="hairline opacity-50 block mb-2" htmlFor="newsletter-email">
              Email
            </label>
            <input
              id="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="invisible-input"
              data-testid="the190-newsletter-email"
              autoComplete="email"
              required
            />
          </div>
          <div className="md:col-span-3">
            <button
              type="submit"
              disabled={submitting}
              className="hairline border border-current px-6 py-3.5 w-full hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500 disabled:opacity-40"
              data-testid="the190-newsletter-submit"
            >
              {submitting ? "…" : "Follow the Story"}
            </button>
          </div>
        </form>
      </Reveal>
    </div>
  );
};

/* --------------------------- FOOTER + NEWSLETTER --------------------------- */

export const FinalCta190 = () => (
  <section data-testid="the190-final-cta" className="bone-section">
    <div className="py-24 md:py-32 border-t border-[color:var(--rule)]">
      <FollowArchive />
    </div>
    <footer className="border-t border-[color:var(--rule)] py-10 px-6 md:px-12 pb-28 md:pb-10">
      <div className="max-w-[1500px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="hairline opacity-55">The Nick Fraser Archive · 1946 — 2023</p>
        <div className="flex items-center gap-8">
          <a
            href="https://www.nickfraserartist.com"
            target="_blank"
            rel="noreferrer"
            className="hairline opacity-55 hover:opacity-90 transition-opacity"
            data-testid="the190-footer-estate"
          >
            nickfraserartist.com
          </a>
          <a
            href="https://www.instagram.com/nickfraserart"
            target="_blank"
            rel="noreferrer"
            className="hairline opacity-55 hover:opacity-90 transition-opacity"
            data-testid="the190-footer-instagram"
          >
            @nickfraserart
          </a>
        </div>
      </div>
    </footer>
  </section>
);

export default Faq190;
